import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import API from "utils/api";
import { useUser } from "utils/hooks";
import { sortByKey } from "utils/misc";

function now() {
  return (new Date()).toISOString();
}

function oldestMessage(messages) {
  const sorted = sortByKey("createdAt", messages);
  return sorted[0]?.createdAt ?? now();
}

export default function useMessages(conversationId) {
  const { token } = useUser();
  const { formatMessage } = useIntl();
  const [before, setBefore] = useState(now);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [messages, setMessages] = useState([]);

  async function fetchMessages({ append, before: beforeOverride }) {
    setLoading(true);

    const { response, data } = await API.fetchMessages(token, conversationId, {
      before: beforeOverride ?? oldestMessage(messages),
    });

    if (response.ok) {
      const newMessages = append
        ? [...data.results, ...messages]
        : [...data.results];

      setMessages(sortByKey("createdAt", newMessages));
      setHasMore(data.results.length === data.limit);
    }

    setLoading(false);
  }

  async function loadMore() {
    const oldestDate = oldestMessage(messages);
    setBefore(oldestDate);
  }

  async function createMessage(formValues, { reset }) {
    const { response, data } = await API.createMessage(
      token,
      conversationId,
      formValues
    );

    if (response.ok) {
      appendMessage(data);
      reset();
    } else {
      (data?.errors || []).forEach((err) => {
        toast.error(formatMessage({ id: err.message }));
      });
    }

    return response.ok;
  }

  async function deleteMessage(id) {
    if (confirm("Are you sure?") === false) {
      return false;
    }

    const { response } = await API.destroyMessage(token, id);

    if (response.ok) {
      const newMessages = messages.filter(m => m.id !== id);
      setMessages(newMessages);
    }
  }

  async function appendMessage(message) {
    if (message?.conversation === conversationId) {
      setMessages(current => [...current, message]);
    }
  }

  useEffect(() => {
    const currentTime = now();
    setBefore(currentTime);
    setMessages([]);
    fetchMessages({ append: false, before: currentTime });
  }, [conversationId]);

  useEffect(() => {
    fetchMessages({ append: true, before });
  }, [before]);

  return {
    messages,
    loading,
    append: appendMessage,
    create: createMessage,
    delete: deleteMessage,
    loadMore,
    hasMore,
  };
}
